import React from 'react';
import { AlarmClockIcon, GlobeIcon, LockIcon, ZapIcon, ShieldCheckIcon, CreditCardIcon } from 'lucide-react';
import { Countdown } from './Countdown';
import { PricingCard } from './PricingCard';
import { TrustLine } from './TrustLine';
import { offer } from '../data/offer';
type OfferSectionProps = {
  variant: 'launch' | 'final';
  showCountdown: boolean;
  showExtrasOffer: boolean;
  guaranteeEnabled: boolean;
};
export function OfferSection({
  variant,
  showCountdown,
  showExtrasOffer,
  guaranteeEnabled
}: OfferSectionProps) {
  const isLaunch = variant === 'launch';
  const headingId = `offer-${variant}`;
  const trust = [{
    icon: LockIcon,
    label: 'Pago seguro'
  }, {
    icon: ZapIcon,
    label: 'Acceso digital tras confirmar el pago'
  }, ...(guaranteeEnabled ? [{
    icon: ShieldCheckIcon,
    label: 'Garantía según condiciones aplicables'
  }] : [])];
  return <section id={isLaunch ? 'oferta' : undefined} aria-labelledby={headingId} className="bg-ink px-5 py-20 sm:py-24" data-id="element-156" data-mp="2y4clcoqbp8qo">
      <div className="mx-auto max-w-5xl text-center" data-id="element-157" data-mp="12o1r8qy1mg28">
        <p className="flex items-center justify-center gap-2 text-sm font-black uppercase tracking-[0.2em] text-sun" data-id="element-158" data-mp="12plcyx2gxjbe">
          <AlarmClockIcon className="h-5 w-5" aria-hidden="true" data-id="element-159" data-mp="sxp1mkbdfwp8" />
          {isLaunch ? 'Precio de lanzamiento' : 'Tu oferta de lanzamiento'}
        </p>
        <h2 id={headingId} className="mt-4 text-3xl font-black uppercase leading-tight text-white sm:text-5xl" data-id="element-160" data-mp="oxon6zo9vli6">
          {isLaunch ? 'Oferta especial de ReadyKit Digital' : 'Elige tu MegaPack hoy'}
        </h2>

        {showCountdown && <div className="mx-auto mt-8 max-w-xl rounded-3xl border-2 border-coral/60 bg-surface px-5 py-6" data-id="element-161" data-mp="1cloatqqaevsj">
            <p className="text-sm font-black uppercase tracking-widest text-coral" data-id="element-162" data-mp="17mv88tcjgp75">La promoción termina en</p>
            <div className="mt-4" data-id="element-163" data-mp="172fxam3e598">
              <Countdown endsAt={offer.countdownEndsAt} durationMinutes={offer.countdownMinutes} data-id="element-164" data-mp="1843ufo7im8d" />
            </div>
          </div>}

        {isLaunch && <div className="mx-auto mt-10 max-w-2xl rounded-3xl bg-surface px-6 py-7" data-id="element-165" data-mp="3tthkkg5xie0z">
            <p className="flex items-center justify-center gap-2 text-sm font-black uppercase tracking-widest text-brand" data-id="element-166" data-mp="2ng760yhr8c6">
              <GlobeIcon className="h-5 w-5" aria-hidden="true" data-id="element-167" data-mp="3ks9ps82mfvbn" />
              Una oferta para toda LATAM
            </p>
            <p className="mt-3 text-2xl font-black uppercase text-white sm:text-3xl" data-id="element-168" data-mp="3otbsiyvqypid">Paga en tu moneda local.</p>
            <p className="mt-2 text-base text-muted" data-id="element-169" data-mp="3b26w05noedc9">
              El precio final se muestra en la moneda habilitada para tu país durante el proceso de pago.
            </p>
          </div>}

        {isLaunch && <h3 className="mt-14 text-2xl font-black uppercase text-white" data-id="element-170" data-mp="313he4cvs4sw0">Elige tu MegaPack</h3>}

        <div className={`mt-10 grid items-stretch gap-6 ${showExtrasOffer ? 'md:grid-cols-2' : 'mx-auto max-w-md'}`} data-id="element-171" data-mp="362c4q0zznlqo">
          <PricingCard title="MegaPack Antipantallas Listo" includes={isLaunch ? '6 tomos + 5 bonos' : '6 Tomos + 5 Bonos · Niños de 5 a 9 años'} idealFor={isLaunch ? 'Familias con niños de 5 a 9 años que buscan actividades preparadas' : undefined} price={offer.price} ctaLabel="Quiero mi MegaPack" href={offer.checkoutUrl} image={offer.mockupImage} data-id="element-172" data-mp="2x3qq2gxn0qv6" />
          {showExtrasOffer && <PricingCard featured title="MegaPack + Extras" includes={`6 tomos + 5 bonos + ${offer.extrasName}`} idealFor={isLaunch ? 'Familias que desean ampliar el sistema con recursos adicionales' : undefined} price={offer.extrasPrice} ctaLabel="Quiero el MegaPack + Extras" href={offer.extrasCheckoutUrl} image={offer.mockupImage} data-id="element-173" data-mp="2ijeb900j325j" />}
        </div>

        <p className="mt-8 flex items-center justify-center gap-2 text-sm font-black uppercase tracking-wide text-brand" data-id="element-174" data-mp="3f7k9n56uwybd">
          <GlobeIcon className="h-4 w-4" aria-hidden="true" data-id="element-175" data-mp="18j1t5yd7ixan" />
          {showExtrasOffer ? 'Ambas ofertas se pagan en moneda local según el checkout.' : 'Se paga en moneda local según el checkout.'}
        </p>

        {!isLaunch && <>
            <div className="mt-6" data-id="element-176" data-mp="1ia4rwtinz06r">
              <TrustLine items={trust} data-id="element-177" data-mp="1ib7it1fzl2mo" />
            </div>
            <div className="mx-auto mt-10 max-w-xl border-t border-line pt-8" data-id="element-178" data-mp="1m958s6hkzsr4">
              <p className="flex items-center justify-center gap-2 text-xl font-black uppercase text-white" data-id="element-179" data-mp="pkxvq16ommoo">
                <CreditCardIcon className="h-6 w-6 text-brand" aria-hidden="true" data-id="element-180" data-mp="ukaiklxjztr9" />
                Paga en tu moneda local
              </p>
              <p className="mt-2 text-base text-muted" data-id="element-181" data-mp="ymytr09qpcre">Disponible para compradores de LATAM en los países habilitados.</p>
            </div>
          </>}
      </div>
    </section>;
}